import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import SEO from '../components/SEO';
import { getBlogPostBySlug, getRelatedPosts } from '../services/firebaseService';
import { FadeInUp } from '../components/Animations';

export default function BlogDetail() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [readingProgress, setReadingProgress] = useState(0);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const data = await getBlogPostBySlug(slug);
        setPost(data);
        if (data?.category) {
          const rel = await getRelatedPosts(data.category, data.id);
          setRelated(rel);
        }
      } catch {
        setPost(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  useEffect(() => {
    const onScroll = () => {
      const total = document.documentElement.scrollHeight - window.innerHeight;
      setReadingProgress(total > 0 ? (window.scrollY / total) * 100 : 0);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  const getTitle = (p) => isRTL && p.titleAr ? p.titleAr : p.title;
  const getExcerpt = (p) => isRTL && p.excerptAr ? p.excerptAr : p.excerpt;
  const getContent = (p) => isRTL && p.contentAr ? p.contentAr : p.content;

  if (loading) {
    return <div className="loading-container" style={{ minHeight: '60vh' }}><div className="spinner" /></div>;
  }

  if (!post) {
    return (
      <div className="not-found">
        <div>
          <h1>404</h1>
          <h2>{t('blogPage.noResults')}</h2>
          <Link to="/blog" className="btn btn-primary">{t('blogPage.backToBlog')}</Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={`${getTitle(post)} | ${isRTL ? 'وجود لينك' : 'Wojod Link'}`}
        description={getExcerpt(post) || ''}
        image={post.image || undefined}
        type="article"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: getTitle(post),
          description: getExcerpt(post) || '',
          image: post.image || undefined,
          datePublished: post.date,
          author: { '@type': 'Organization', name: 'Wojod Link' },
          publisher: {
            '@type': 'Organization',
            name: 'Wojod Link',
            logo: { '@type': 'ImageObject', url: 'https://wojodlink.com/og-image.png' }
          }
        }}
      />

      {/* Reading Progress */}
      <div className="reading-progress-bar" style={{ width: `${readingProgress}%` }} />

      {/* Page Hero */}
      <section className="page-hero">
        <div className="container">
          <FadeInUp>
            <div className="page-hero-content">
              <Link to="/blog" className="back-to-blog">
                <i className={`fas fa-arrow-${isRTL ? 'right' : 'left'}`} />
                {t('blogPage.backToBlog')}
              </Link>
              <h1 className="page-title">{getTitle(post)}</h1>
              <div className="blog-detail-meta">
                <span><i className="far fa-calendar" /> {post.date}</span>
                {post.category && <span><i className="far fa-folder" /> {post.category}</span>}
                {post.readTime && <span><i className="far fa-clock" /> {post.readTime} {t('blogPage.minRead')}</span>}
              </div>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* Article */}
      <section className="blog-detail">
        <div className="container">
          <div className="blog-detail-content">
            {/* Cover image */}
            {post.image && (
              <FadeInUp>
                <div className="blog-detail-media">
                  <img src={post.image} alt={getTitle(post)} />
                </div>
              </FadeInUp>
            )}

            {/* Body */}
            <FadeInUp delay={0.1}>
              <div
                className="blog-detail-body"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(getContent(post) || '') }}
              />
            </FadeInUp>

            {/* Share */}
            <FadeInUp delay={0.2}>
              <div className="blog-share">
                <span className="share-label">{t('blogPage.shareArticle')}</span>
                <div className="share-buttons">
                  <a
                    href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(getTitle(post))}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="share-btn"
                    aria-label="Share on Twitter"
                  >
                    <i className="fab fa-x-twitter" />
                  </a>
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="share-btn"
                    aria-label="Share on Facebook"
                  >
                    <i className="fab fa-facebook-f" />
                  </a>
                  <a
                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="share-btn"
                    aria-label="Share on LinkedIn"
                  >
                    <i className="fab fa-linkedin-in" />
                  </a>
                  <a
                    href={`https://wa.me/?text=${encodeURIComponent(getTitle(post) + ' ' + shareUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="share-btn"
                    aria-label="Share on WhatsApp"
                  >
                    <i className="fab fa-whatsapp" />
                  </a>
                </div>
              </div>
            </FadeInUp>

            {/* Related */}
            {related.length > 0 && (
              <FadeInUp delay={0.25}>
                <div className="related-posts">
                  <h2 className="section-title">{t('blogPage.relatedPosts')}</h2>
                  <div className="related-posts-grid">
                    {related.map((rp) => (
                      <Link key={rp.id} to={`/blog/${rp.slug}`}>
                        <div className="related-post-card">
                          <div className="related-post-thumb">
                            {rp.image ? (
                              <img src={rp.image} alt={isRTL && rp.titleAr ? rp.titleAr : rp.title} loading="lazy" />
                            ) : (
                              <div style={{ width: '100%', height: '100%', background: 'var(--color-dark-card-hover)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <i className="fas fa-image" style={{ color: 'rgba(255,255,255,0.15)' }} />
                              </div>
                            )}
                          </div>
                          <div className="related-post-content">
                            <h3>{isRTL && rp.titleAr ? rp.titleAr : rp.title}</h3>
                            <p>{rp.date}</p>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </FadeInUp>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
